import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import { useDarkMode } from '../hooks/useDarkMode'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const closeTimeout = useRef(null)
  const { isDarkMode, toggleDarkMode } = useDarkMode()
  const location = useLocation()

  const openDropdown = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
    setServicesOpen(true)
  }, [])

  const closeDropdown = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setServicesOpen(false)
    }, 150)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
    setServicesOpen(false)
    if (closeTimeout.current) clearTimeout(closeTimeout.current)
  }, [location])

  useEffect(() => {
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current)
    }
  }, [])

  const navigation = [
    {
      name: 'Home',
      href: '/',
      current: location.pathname === '/'
    },
    {
      name: 'Leistungen',
      href: '/services',
      current: location.pathname.startsWith('/services'),
      dropdown: true,
      items: [
        { name: 'Webdesign & Entwicklung', href: '/services/web' },
        { name: 'E-Commerce & Shopify', href: '/services/ecommerce' },
        { name: 'Suchmaschinenoptimierung (SEO)', href: '/services/seo' },
        { name: 'Domains & Hosting', href: '/services/domains' },
        { name: 'E-Mail Security', href: '/services/email-security' },
        { name: 'Digital Signage', href: '/services/digital-signage' },
        { name: 'Print Design', href: '/services/print-design' }
      ]
    },
    {
      name: 'Über uns',
      href: '/about',
      current: location.pathname === '/about'
    },
    {
      name: 'Portfolio',
      href: '/portfolio',
      current: location.pathname === '/portfolio'
    },
    {
      name: 'Projekt-Check',
      href: '/termin',
      current: location.pathname === '/termin'
    }
  ]

  const Logo = () => {
    const lightLogo = '/images/logo/logo-light.png'
    const darkLogo = '/images/logo/logo-dark.png'

    return (
      <Link
        to="/"
        className="flex items-center gap-3 group"
        aria-label="Cryptedmoon Homepage"
      >
        <picture>
          <source srcSet={isDarkMode ? '/images/logo/logo-light.webp' : '/images/logo/logo-dark.webp'} type="image/webp" />
          <img
            src={isDarkMode ? lightLogo : darkLogo}
            alt="cryptedmoon Logo"
            width="180"
            height="36"
            className="h-7 w-auto sm:h-8"
          />
        </picture>
      </Link>
    )
  }

  const NavLink = ({ item, mobile = false }) => {
    if (item.dropdown) {
      if (mobile) {
        return (
          <div className="relative w-full">
            <button
              className={`
                flex items-center justify-between w-full px-3 py-2.5 text-[15px] font-medium rounded-xl
                ${item.current
                  ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-950/40'
                  : 'text-dark-600 dark:text-dark-300 hover:text-dark-900 dark:hover:text-white hover:bg-dark-50 dark:hover:bg-dark-800'
                }
                transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
              `}
              onClick={() => setServicesOpen(!servicesOpen)}
              aria-expanded={servicesOpen}
              aria-haspopup="menu"
            >
              {item.name}
              <ChevronDownIcon
                className={`w-4 h-4 transition-transform duration-150 ${servicesOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.15 }}
                  className="mt-1 ml-3 space-y-0.5"
                  role="menu"
                >
                  {item.items.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      className="block px-3 py-2 text-sm text-dark-500 dark:text-dark-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-150 rounded-lg"
                      role="menuitem"
                      onClick={() => {
                        setServicesOpen(false)
                        setIsOpen(false)
                      }}
                    >
                      {subItem.name}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      }

      return (
        <div
          className="relative"
          onMouseEnter={openDropdown}
          onMouseLeave={closeDropdown}
        >
          <Link
            className={`
              flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg
              ${item.current
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-dark-500 dark:text-dark-400 hover:text-dark-900 dark:hover:text-white'
              }
              transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
            `}
            to={item.href}
            aria-haspopup="menu"
            aria-expanded={servicesOpen}
          >
            {item.name}
            <ChevronDownIcon
              className={`w-3.5 h-3.5 transition-transform duration-150 ${servicesOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </Link>

          <AnimatePresence>
            {servicesOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full left-0 pt-2 w-56"
              >
                <div className="bg-white dark:bg-dark-800 rounded-xl shadow-elevated border border-dark-100 dark:border-dark-700 py-1.5" role="menu">
                  {item.items.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      className="block px-4 py-2 text-sm text-dark-600 dark:text-dark-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-dark-50 dark:hover:bg-dark-700/50 transition-colors duration-150"
                      role="menuitem"
                      onClick={() => {
                        setServicesOpen(false)
                        setIsOpen(false)
                      }}
                    >
                      {subItem.name}
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )
    }

    return (
      <Link
        to={item.href}
        className={`
          ${mobile
            ? 'block px-3 py-2.5 text-[15px] font-medium rounded-xl'
            : 'px-3 py-2 text-sm font-medium rounded-lg'
          }
          ${item.current
            ? 'text-primary-600 dark:text-primary-400' + (mobile ? ' bg-primary-50 dark:bg-primary-950/40' : '')
            : 'text-dark-500 dark:text-dark-400 hover:text-dark-900 dark:hover:text-white' + (mobile ? ' hover:bg-dark-50 dark:hover:bg-dark-800' : '')
          }
          transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
        `}
        aria-current={item.current ? 'page' : undefined}
      >
        {item.name}
      </Link>
    )
  }

  return (
    <>
      {/* Skip Navigation Link for Accessibility */}
      <a
        href="#main-content"
        className="skip-link"
        onFocus={(e) => e.target.style.transform = 'translateY(0)'}
        onBlur={(e) => e.target.style.transform = 'translateY(-100%)'}
      >
        Zum Hauptinhalt springen
      </a>

      <nav
        className={`
          fixed top-0 left-0 right-0 z-50 transition-all duration-200
          ${isScrolled
            ? 'bg-white/90 dark:bg-dark-900/90 backdrop-blur-xl border-b border-dark-200/40 dark:border-dark-700/40'
            : 'bg-transparent'
          }
        `}
        role="navigation"
        aria-label="Hauptnavigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <Logo />

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:gap-1">
              <div className="flex items-center gap-0.5">
                {navigation.map((item) => (
                  <NavLink key={item.name} item={item} />
                ))}
              </div>

              <div className="w-px h-5 bg-dark-200 dark:bg-dark-700 mx-3" aria-hidden="true" />

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 text-dark-400 dark:text-dark-500 hover:text-dark-600 dark:hover:text-dark-300 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 cursor-pointer"
                aria-label={isDarkMode ? 'Auf hellen Modus wechseln' : 'Auf dunklen Modus wechseln'}
              >
                {isDarkMode ? (
                  <SunIcon className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <MoonIcon className="w-5 h-5" aria-hidden="true" />
                )}
              </button>

              {/* CTA Button */}
              <Link
                to="/termin"
                className="ml-2 inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                Projekt-Check
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-1">
              <button
                onClick={toggleDarkMode}
                className="p-2 text-dark-400 dark:text-dark-500 hover:text-dark-600 dark:hover:text-dark-300 rounded-lg transition-colors duration-150 cursor-pointer"
                aria-label={isDarkMode ? 'Auf hellen Modus wechseln' : 'Auf dunklen Modus wechseln'}
              >
                {isDarkMode ? (
                  <SunIcon className="w-5 h-5" />
                ) : (
                  <MoonIcon className="w-5 h-5" />
                )}
              </button>

              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-dark-500 dark:text-dark-400 hover:text-dark-700 dark:hover:text-dark-200 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 cursor-pointer"
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
                aria-label="Menü öffnen/schließen"
              >
                {isOpen ? (
                  <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="w-6 h-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              id="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
              className="lg:hidden bg-white dark:bg-dark-900 border-t border-dark-100 dark:border-dark-800"
            >
              <div className="px-4 py-3 space-y-0.5">
                {navigation.map((item) => (
                  <NavLink key={item.name} item={item} mobile />
                ))}

                <div className="pt-3 mt-2 border-t border-dark-100 dark:border-dark-800">
                  <Link
                    to="/termin"
                    className="flex items-center justify-center w-full px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors duration-150"
                    onClick={() => setIsOpen(false)}
                  >
                    Projekt-Check buchen
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  )
}

export default Navbar
